import { SubscriptionState } from '../../types';
import { EnhancedDeliverySchedule } from '../../hooks/useSubscriptionData';
import QuickActions from './QuickActions';
import SubscriptionDetails from './SubscriptionDetails';
import DeliveryHistorySection from './DeliveryHistorySection';
import PaymentBillingSection from './PaymentBillingSection';
import SubscriptionActions from './SubscriptionActions';
import { formatAddress } from '../../utils/subscriptionUtils';
import { useNavigate } from 'react-router-dom';
import { Clock, Calendar, RotateCw, ChevronRight, Users } from 'lucide-react';
import { FEATURES } from '../../config';
import { Address } from '../../types/api';
import { useState, useEffect, lazy, Suspense } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import GuideLinks from './GuideLinks';

// Add debug logging function
const logAddressInfo = (address: any, prefix: string) => {
  console.log(`${prefix} Address debug:`, {
    hasAddress: !!address,
    id: address?.id,
    addressId: address?.address_id,
    street: address?.street,
    city: address?.city,
    state: address?.state,
    zipCode: address?.zipCode,
    allKeys: address ? Object.keys(address).join(', ') : 'none'
  });
};

interface SubscriptionMainContentProps {
  activeSubscription: SubscriptionState;
  upcomingDeliveries: EnhancedDeliverySchedule[];
  pastDeliveries: EnhancedDeliverySchedule[];
  activeTab: 'upcoming' | 'past';
  setActiveTab: (tab: 'upcoming' | 'past') => void;
  processingDeliveryId: string | null;
  loading: boolean;
  onSkip: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onUpgrade: () => void;
  onRenew: () => void;
  onSkipDeliveryFromHistory: (deliveryId: string, date: string) => void;
  onAddressUpdate: (address: Address) => void;
  hideTitle?: boolean;
}

interface Delivery {
  id: string;
  date: string;
  status: 'scheduled' | 'delivered' | 'skipped' | 'processing';
  items?: DeliveryItem[];
}

interface DeliveryItem {
  id: string;
  name: string;
  quantity: number;
  price?: number;
}

// Lazy load the modal component
const ScheduleSetupModal = lazy(() => import('../ScheduleSetupModal'));

const SubscriptionMainContent: React.FC<SubscriptionMainContentProps> = ({
  activeSubscription,
  upcomingDeliveries,
  pastDeliveries,
  activeTab,
  setActiveTab,
  processingDeliveryId,
  loading,
  onSkip,
  onPause,
  onResume,
  onCancel,
  onUpgrade,
  onRenew,
  onSkipDeliveryFromHistory,
  onAddressUpdate,
  hideTitle = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [extractedDeliverySettings, setExtractedDeliverySettings] = useState<{
    day_of_week: number | undefined;
    time_slots: string[];
  }>({
    day_of_week: 1,
    time_slots: ['9:00 AM - 12:00 PM']
  });
  
  // Log the subscription status for debugging
  console.log('SubscriptionMainContent rendering with status:', activeSubscription.status);

  // Determine the effective status to display
  const getEffectiveStatus = () => {
    // Check if subscription should show as pending renewal
    if (activeSubscription.status === 'active' && 
        activeSubscription.isRecurring === false && 
        activeSubscription.remainingDeliveries === 0) {
      return 'pending_renewal';
    }
    return activeSubscription.status;
  };

  const effectiveStatus = getEffectiveStatus();

  // Extract delivery settings function
  const extractDeliverySettings = () => {
    const settings = {
      day_of_week: undefined as number | undefined,
      time_slots: [] as string[]
    };
    
    // Check all possible property paths
    if (activeSubscription.deliverySettings?.day_of_week !== undefined) {
      settings.day_of_week = activeSubscription.deliverySettings.day_of_week;
    }
    
    if (activeSubscription.deliverySettings?.time_slots && 
        activeSubscription.deliverySettings.time_slots.length > 0) {
      settings.time_slots = activeSubscription.deliverySettings.time_slots;
    }
    
    // Provide defaults if still not found
    if (settings.day_of_week === undefined) {
      settings.day_of_week = 1; // Default to Monday
    }
    
    if (settings.time_slots.length === 0) {
      settings.time_slots = ['9:00 AM - 12:00 PM']; // Default time slot
    }
    
    return settings;
  };
  
  // Update extracted settings when subscription changes
  useEffect(() => {
    const settings = extractDeliverySettings();
    console.log('Updating delivery settings from subscription:', settings);
    setExtractedDeliverySettings(settings);
  }, [activeSubscription]);

  const handleScheduleModalOpen = () => {
    // Log the extracted settings for debugging
    console.log('Using delivery settings for modal:', extractedDeliverySettings);
    setShowScheduleModal(true);
  };

  const handleScheduleModalClose = () => {
    setShowScheduleModal(false);
  };

  const handleScheduleSave = (scheduleData: any) => {
    console.log('Schedule updated:', scheduleData);
    
    // Handle the schedule update (similar to what would happen after navigation)
    if (scheduleData.scheduleUpdated) {
      // You might want to trigger a refresh of the subscription data here
      // For now, just show a success message
      toast.success('Delivery schedule updated successfully!');
    }
    
    setShowScheduleModal(false);
  };

  // Enhanced address handling
  const handleAddressUpdate = (address: Address) => {
    // Ensure address has a valid ID
    if (!address.id && (address as any).address_id) {
      console.log('Fixing address object - adding id from address_id');
      address = {
        ...address,
        id: (address as any).address_id
      };
    }
    
    // Log the address details for debugging
    logAddressInfo(address, 'handleAddressUpdate');
    
    // Only update if we have a valid ID
    if (address.id) {
      onAddressUpdate(address);
    } else {
      console.error('Attempted to update address without valid ID:', address);
    }
  };

  // Filter deliveries for the selected subscription only
  const filteredUpcomingDeliveries = upcomingDeliveries.filter(
    delivery => delivery.subscriptionId === activeSubscription.id
  );

  return (
    <div className="p-0">
      
      {!hideTitle && <h2 className="text-lg font-semibold mb-4">Your Subscriptions</h2>}

      <SubscriptionDetails 
        subscription={{
          ...activeSubscription,
          deliverySettings: activeSubscription.deliverySettings || undefined,
          endDate: activeSubscription.endDate || null,
          isRecurring: activeSubscription.isRecurring || false,
          remaining_deliveries: activeSubscription.remainingDeliveries || 0,
        }}
        onAddressUpdate={handleAddressUpdate}
      />

      {(effectiveStatus === 'active' || effectiveStatus === 'pending_renewal') && activeSubscription.isRecurring !== true && (
        <div className="mb-4">
          <div 
            onClick={FEATURES.ENABLE_RENEWALS ? async () => {
              try {
                await onRenew();
                // The redirect will happen in the context, no need for additional actions here
              } catch (error) {
                toast.error(t('subscription.actions.renewError'));
                console.error('Failed to initiate renewal:', error);
              }
            } : undefined}
            className={`px-4 py-3 flex items-center ${FEATURES.ENABLE_RENEWALS ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} ${
              effectiveStatus === 'pending_renewal' || 
              (activeSubscription.remainingDeliveries !== undefined && activeSubscription.remainingDeliveries <= 2) 
                ? 'bg-amber-50 hover:bg-amber-100' 
                : 'hover:bg-amber-50'
            }`}
          >
            <div className={`w-10 h-10 ${
              effectiveStatus === 'pending_renewal' || 
              (activeSubscription.remainingDeliveries !== undefined && activeSubscription.remainingDeliveries <= 2) 
                ? 'bg-amber-200' 
                : 'bg-amber-100'
            } rounded-full flex items-center justify-center mr-4`}>
              <RotateCw className={`w-6 h-6 ${
                effectiveStatus === 'pending_renewal' || 
                (activeSubscription.remainingDeliveries !== undefined && activeSubscription.remainingDeliveries <= 2) 
                  ? 'text-amber-600' 
                  : 'text-amber-500'
              }`} />
            </div>
            <div className='flex flex-col flex-1'>
              <span className={`font-medium text-base ${
                effectiveStatus === 'pending_renewal' || 
                (activeSubscription.remainingDeliveries !== undefined && activeSubscription.remainingDeliveries <= 2) 
                  ? 'text-amber-700' 
                  : ''
              }`}>
                {FEATURES.ENABLE_RENEWALS ? t('subscription.actions.renewSubscription') : t('common.serviceUnavailable')}
              </span>
              <span className='text-xs text-gray-400'>
                {effectiveStatus === 'pending_renewal'
                  ? t('subscription.actions.renewSoonMessage')
                  : activeSubscription.remainingDeliveries !== undefined && activeSubscription.remainingDeliveries <= 2 
                    ? activeSubscription.remainingDeliveries === 1 
                      ? t('subscription.actions.renewSoonMessage') 
                      : t('subscription.actions.renewLowMessage')
                    : t('subscription.actions.renewDescription')}
              </span>
              {(effectiveStatus === 'pending_renewal' || 
                (activeSubscription.remainingDeliveries !== undefined && activeSubscription.remainingDeliveries <= 2)) && (
                <span className='text-xs font-semibold text-amber-600 mt-1'>
                  {effectiveStatus === 'pending_renewal'
                    ? t('subscription.actions.deliveriesRemaining', { count: 0 })
                    : t('subscription.actions.deliveriesRemaining', { count: activeSubscription.remainingDeliveries })}
                </span>
              )}
            </div>
            <ChevronRight className={`w-5 h-5 ${
              effectiveStatus === 'pending_renewal' || 
              (activeSubscription.remainingDeliveries !== undefined && activeSubscription.remainingDeliveries <= 2) 
                ? 'text-amber-500' 
                : 'text-gray-400'
            }`} />
          </div>
        </div>
      )}

      {activeSubscription.status !== 'cancelled' && activeSubscription.id && activeSubscription.plan && activeSubscription.address && (
        <QuickActions
          key={`quick-actions-${effectiveStatus || 'active'}-${activeSubscription.id}`}
          subscription={{
            id: activeSubscription.id,
            status: activeSubscription.status || 'active',
            plan: activeSubscription.plan,
            serviceType: activeSubscription.serviceType || 'flower',
            address: activeSubscription.address,
            nextDeliveryDate: activeSubscription.nextDeliveryDate || '',
            startDate: activeSubscription.startDate || '',
            endDate: activeSubscription.endDate || '',
            price: activeSubscription.price || 0,
            deliverySettings: activeSubscription.deliverySettings || undefined
          }}
          onSkip={onSkip}
          onEditSchedule={() => {
            console.log(`Opening schedule modal for subscription ID: ${activeSubscription.id}`);
            console.log('Delivery settings:', {
              deliverySettings: activeSubscription.deliverySettings,
              day_of_week: activeSubscription.deliverySettings?.day_of_week,
              time_slots: activeSubscription.deliverySettings?.time_slots
            });
            handleScheduleModalOpen();
          }}
          onPause={onPause}
          onResume={onResume}
          onCancel={onCancel}
          onUpgrade={onUpgrade}
          onRenew={onRenew}
          status={effectiveStatus}
          setIsModalOpen={() => {}}
          activeSubscription={activeSubscription}
        />
      )}

      <div className='p-3 bg-blue-100 rounded-lg mx-4 my-5 flex items-center'>
        <div className='flex items-center'>
        <Users size={32} className="mr-3 text-blue-600" />
        <span className='text-sm mr-2 font-medium'>{t('subscription.subscriptionDetails.referralProgram')}</span>
        </div>
       <button
            onClick={() => navigate('/referral-program')}
            className="text-xs bg-blue-300 px-3 py-1 rounded-full border border-blue-200 h-fit"
          >
            {t('subscription.subscriptionDetails.buttons.more')}
          </button>
       </div>

      {effectiveStatus !== 'cancelled' && (
        <DeliveryHistorySection
          upcomingDeliveries={upcomingDeliveries}
          pastDeliveries={pastDeliveries}
          activeTab={activeTab}
          onChangeTab={setActiveTab}
          onSkipDelivery={onSkipDeliveryFromHistory}
          subscription={activeSubscription}
          processingDeliveryId={processingDeliveryId}
          loading={loading}
        />
      )}

      {/* Schedule Setup Modal */}
      {(() => {
        console.log('Rendering ScheduleSetupModal with delivery settings:', {
          deliverySettings: activeSubscription.deliverySettings,
          extractedSettings: extractedDeliverySettings,
          fullSubscription: activeSubscription
        });
        return null;
      })()}
      {showScheduleModal && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-xl">Loading schedule options...</div>
        </div>}>
          <ScheduleSetupModal
            isOpen={showScheduleModal}
            onClose={handleScheduleModalClose}
            initialData={{ 
              limitedView: true,
              fromSubscription: true,
              selectedAddress: activeSubscription.address,
              isWeeklySubscription: true,
              currentDeliverySetting: extractedDeliverySettings,
              weeklyDay: extractedDeliverySettings.day_of_week,
              selectedTimeSlots: extractedDeliverySettings.time_slots,
              returnTo: '/subscription-management',
              subscriptionId: activeSubscription.id,
              currentSchedule: {
                dayOfWeek: extractedDeliverySettings.day_of_week || 1,
                timeSlots: extractedDeliverySettings.time_slots
              }
            }}
            onSave={handleScheduleSave}
            currentDeliveries={filteredUpcomingDeliveries}
          />
        </Suspense>
      )}

      {/* <PaymentBillingSection /> */}

      <GuideLinks subscription={activeSubscription} />
    </div>
  );
};

export default SubscriptionMainContent; 